// 1) Import React, Sentry, and any Sentry integrations at the top.
import { createRoot } from 'react-dom/client';
import * as Sentry from '@sentry/react';
import { HelmetProvider } from 'react-helmet-async';

import reportWebVitals from '@/report-web-vitals';
import App from '@/App';
import { enforceMainDomain } from '@/utils/enforceMainDomain';

// 2) Initialize Sentry BEFORE rendering your React app.
//    - Adjust DSN, sample rates, etc. as needed.
Sentry.init({
  dsn: 'https://f7c8554b4aac5d5d1fcea9189a12f0ac@o4508337507598336.ingest.us.sentry.io/4508637050437632',
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      // Only capture replays on errors, not every session
      maskAllText: true,
      blockAllMedia: true,
    }),
  ],
  // Reduce sample rates to prevent rate limiting
  tracesSampleRate: 0.1, // Only 10% of transactions
  tracePropagationTargets: ['localhost', /^https:\/\/fastapi-entry-point-4slfwjwq6q-uc\.a\.run\.app\/?/],
  replaysSessionSampleRate: 0, // Don't record normal sessions
  replaysOnErrorSampleRate: 0.1, // Only 10% of error sessions

  // Filter out noisy errors
  ignoreErrors: [
    // Firebase auth errors that are expected
    'auth/popup-closed-by-user',
    'auth/cancelled-popup-request',
    'auth/popup-blocked',
    'INTERNAL ASSERTION FAILED',
    'Cross-Origin-Opener-Policy',

    // Chrome extension errors
    'chrome-extension://',
    'Extension context invalidated',
    'Failed to fetch',

    // Network errors
    'NetworkError',
    'Network request failed',
    'Load failed',
    'auth/network-request-failed',

    // Firebase offline errors (environmental noise)
    'Failed to get document because the client is offline',

    // Third-party Drip tracking script race condition (JSONP callback undefined)
    /Drip_\d+ is not a function/,

    // Common browser errors
    'ResizeObserver loop limit exceeded',
    'ResizeObserver loop completed',
    'Non-Error promise rejection captured',
  ],

  // Filter out extension-related resource fetch breadcrumbs
  beforeBreadcrumb(breadcrumb) {
    if (breadcrumb.category === 'fetch' || breadcrumb.category === 'xhr') {
      const url = breadcrumb.data?.url || '';
      if (typeof url === 'string' && url.startsWith('chrome-extension://')) {
        return null;
      }
    }
    return breadcrumb;
  },

  // Filter out transactions we don't care about
  beforeSend(event) {
    // Drop events from localhost/dev environments — these should never reach production Sentry
    const eventUrl = event.tags?.url || event.request?.url || '';
    if (typeof eventUrl === 'string' && (eventUrl.includes('localhost') || eventUrl.includes('127.0.0.1'))) {
      return null;
    }

    // Filter out extension-related errors (all chrome-extension:// URLs)
    if (event.exception?.values?.[0]?.value?.includes('chrome-extension://')) {
      return null;
    }

    // Filter out Firebase internal errors
    if (event.exception?.values?.[0]?.value?.includes('Pending promise was never set')) {
      return null;
    }

    // Filter out COOP errors
    if (event.exception?.values?.[0]?.value?.includes('window.closed')) {
      return null;
    }

    // Filter out Firebase offline errors (environmental noise, not bugs)
    const errorValue = event.exception?.values?.[0]?.value || '';
    if (
      errorValue.includes('client is offline') ||
      (errorValue.includes('Failed to get document') && errorValue.toLowerCase().includes('offline'))
    ) {
      return null;
    }

    return event;
  },
});

// Enforce main domain before rendering the app
enforceMainDomain();

// 3) Create the root and render your App as usual.
const root = createRoot(document.getElementById('root') as HTMLElement);
root.render(
  <HelmetProvider>
    <App />
  </HelmetProvider>,
);

// 4) Optional: continue measuring performance in your app as needed.
reportWebVitals();

// 5) Register minimal service worker for PWA installability (no caching).
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => {
      // SW registration can fail in incognito mode or restricted environments — safe to ignore.
    });
  });
}
